import { Box } from '@mui/material';
import { ReactNode, useEffect } from 'react';
import { type SegmentationListingPageProps } from '@/components/pages/segmentationListings/segmentationListingPageProps';
import { SeoGlobalNavbar } from '@/components/common/SeoGlobalNavbar';
import {
  LaunchDarklyEvaluationReasons,
  ScreenName,
  ScreenTemplate,
  TealiumSlot,
  UI_FEATURE_FLAGS,
} from '@/constants';
import {
  AmplitudeEvent,
  setDeviceDataFromHeaders,
  setPagePropertiesFromData,
  trackAmplitudeEvent,
} from '@/lib/analyticsHelper';
import { useLDClientFlags } from '@/components/hooks/useLDClientFlags';
import { WhenCareModal } from './modals/WhenCareModal';
import { PageMeta } from './PageMeta/PageMeta';
import { trackPageViewedEvent } from './helpers/trackPageViewedEvent';

export interface SearchDirectoryLayoutProps {
  children?: ReactNode;
  pageData: SegmentationListingPageProps['pageData'];
  enrollmentURL: string;
  deviceChar: SegmentationListingPageProps['deviceChar'];
  testName: string;
}

export const SearchDirectoryLayout = ({
  children,
  enrollmentURL,
  deviceChar,
  pageData,
  testName,
}: SearchDirectoryLayoutProps) => {
  const { seoSegmentationSearchResults, seoSegmentationDetails, seoNavigationLinkSection } =
    pageData;

  const {
    pageType,
    serviceIdForMember,
    conversionHeader,
    seoPageMetadata: {
      canonicalURL,
      metaDescriptions,
      title,
      hrefLangLinks,
      metaKeywords,
      seoPageId,
    },
  } = seoSegmentationDetails;

  const {
    pagination: { currentPage, numberOfPages },
    tests: seoBackEndTests,
  } = seoSegmentationSearchResults;

  const {
    SEO_3708_REVIEWS_SECTION_SEO: reviewsAboveSearchResultsFlag,
    SEO_3169_SALC_ROBUST_PROVIDER_CARDS: salcRobustProviderCardFlag,
    SEO_3217_ABOVE_THE_FOLD_CONVERSION_TEST_PROVIDERS: aboveTheFoldTestJobsFlag,
    GROWTH_ENROLLMENT_SEEKER_ADULT_CARE_ROLLOUT: growthEnrollmentSeekerAdultCareRolloutFlag,
  } = useLDClientFlags();

  useEffect(() => {
    const pageProperties = {
      screen_name: ScreenName.SEGMENTATION,
      screen_template: ScreenTemplate.SEGMENTATION,
      seo_page_id: parseInt(seoPageId, 10),
      service_id_for_member: serviceIdForMember,
      slots: TealiumSlot.VISITOR,
    };
    if (deviceChar && Object.keys(deviceChar).length > 0) {
      setPagePropertiesFromData(pageProperties);
      setDeviceDataFromHeaders(deviceChar);
    }
  }, [deviceChar, seoPageId, serviceIdForMember]);

  useEffect(() => {
    trackPageViewedEvent({
      pageTitle: seoSegmentationDetails.headline,
      serviceIdForMember,
      amplitudeTestName: testName,
      searchingFor: seoSegmentationDetails.searchingFor,
      pageType,
      seoSegmentationSearchResults,
    });

    if (seoBackEndTests) {
      seoBackEndTests.forEach((seoBETest) =>
        trackAmplitudeEvent(AmplitudeEvent.testExposure, {
          test_name: seoBETest.testName,
          test_variant: seoBETest.testVariant,
          service_id_for_member: seoSegmentationDetails.serviceIdForMember,
          page_template: pageType,
        })
      );
    }

    if (
      salcRobustProviderCardFlag &&
      salcRobustProviderCardFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.SEO_3169_SALC_ROBUST_PROVIDER_CARDS.id,
        test_variant: salcRobustProviderCardFlag.variationIndex,
        service_id_for_member: seoSegmentationDetails.serviceIdForMember,
        page_template: pageType,
      });
    }
    if (
      aboveTheFoldTestJobsFlag &&
      aboveTheFoldTestJobsFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.SEO_3217_ABOVE_THE_FOLD_CONVERSION_TEST_PROVIDERS.id,
        test_variant: aboveTheFoldTestJobsFlag.variationIndex,
        service_id_for_member: seoSegmentationDetails.serviceIdForMember,
        page_template: pageType,
      });
    }
    if (
      reviewsAboveSearchResultsFlag &&
      reviewsAboveSearchResultsFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.SEO_3708_REVIEWS_SECTION_SEO.id,
        test_variant: reviewsAboveSearchResultsFlag.variationIndex,
        service_id_for_member: seoSegmentationDetails.serviceIdForMember,
        page_template: pageType,
      });
    }
    if (
      growthEnrollmentSeekerAdultCareRolloutFlag &&
      growthEnrollmentSeekerAdultCareRolloutFlag.reason.kind ===
        LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.GROWTH_ENROLLMENT_SEEKER_ADULT_CARE_ROLLOUT.id,
        test_variant: growthEnrollmentSeekerAdultCareRolloutFlag.variationIndex,
        service_id_for_member: seoSegmentationDetails.serviceIdForMember,
        page_template: pageType,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PageMeta
        title={title}
        canonicalUrl={canonicalURL}
        metaDescriptions={metaDescriptions}
        metaKeywords={metaKeywords}
        hrefLangLinks={hrefLangLinks}
        paginationPageCount={numberOfPages}
        paginationCurrentPage={currentPage}
      />
      <SeoGlobalNavbar seoNavigationLinkSection={seoNavigationLinkSection} />
      <Box>{children}</Box>
      <WhenCareModal
        enrollmentURL={enrollmentURL}
        pageType={pageType}
        serviceIdForMember={serviceIdForMember}
        modalTitle={conversionHeader}
      />
    </>
  );
};
